/**
 * What a file needs, and what the person answered (spec 102).
 *
 * Pure, and deliberately free of the generated client. Two reasons: the
 * grouping below is worth proving without rendering anything, and the contract
 * these shapes mirror is not in this repository yet, so typing against it
 * directly would block the screen on a service change still in review
 * (research R1, R2).
 *
 * `UnresolvedReferenceLike` is structurally what the service sends. When the
 * generated model arrives it satisfies this shape, and the only change here is
 * to say so.
 */

/** The two kinds the service defines. There is no third and will not be. */
export type ReferenceKind = 'llm_profile' | 'assignee';

/** One reference, as the service reports it: per step. */
export interface UnresolvedReferenceLike {
  kind: ReferenceKind;
  value: string;
  /**
   * Absent and null mean the same thing here: a reference on a step with no
   * usable id. The generated model makes it optional because the service
   * defaults it, so this accepts both rather than making every caller
   * normalise one into the other.
   */
  step_id?: string | null;
  blocking: boolean;
}

/** One row on the screen: per distinct thing to answer. */
export interface ReferenceRow {
  kind: ReferenceKind;
  /** Exactly as the file spells it, so it can be found in the file. */
  value: string;
  /** Every step this row covers, in the order the service reported them. */
  stepIds: string[];
  blocking: boolean;
  /** Stable across renders and distinct between rows. */
  key: string;
}

/**
 * Chosen instead of leaving a gate to the file's absent person.
 *
 * A real value rather than an absent key, because absence already means
 * "nobody has touched this row" and the two must not look the same: only one
 * of them counts as answered (data-model.md).
 */
export const LEAVE_UNASSIGNED = '\u0000leave-unassigned';

export interface Answers {
  /** The file's value -> the chosen profile's slug. Absent means unanswered. */
  profiles: Record<string, string | undefined>;
  /** Step -> the chosen person's address, or `LEAVE_UNASSIGNED`. */
  assignees: Record<string, string | undefined>;
}

/**
 * The service's per-step references, as rows.
 *
 * A model reference collapses by value: one answer covers every step that named
 * it, and the row carries them all because that list is how somebody finds them
 * in their file. A person stays per step, because two gates naming one absent
 * person may want different substitutes — which is also how the service keys
 * them, so the shape sent back matches the shape asked for.
 */
export function toRows(
  references: readonly UnresolvedReferenceLike[],
): ReferenceRow[] {
  const rows: ReferenceRow[] = [];
  const byValue = new Map<string, ReferenceRow>();

  for (const reference of references) {
    const stepId = reference.step_id ?? null;
    const steps = stepId === null ? [] : [stepId];

    if (reference.kind === 'assignee') {
      rows.push({
        kind: 'assignee',
        value: reference.value,
        stepIds: steps,
        blocking: reference.blocking,
        key: `assignee:${stepId ?? ''}`,
      });
      continue;
    }

    const existing = byValue.get(reference.value);
    if (existing) {
      existing.stepIds.push(...steps);
      continue;
    }
    const row: ReferenceRow = {
      kind: 'llm_profile',
      value: reference.value,
      stepIds: steps,
      blocking: reference.blocking,
      key: `llm_profile:${reference.value}`,
    };
    byValue.set(reference.value, row);
    rows.push(row);
  }

  return rows;
}

/** What is answered before anybody touches anything. */
export function initialAnswers(rows: readonly ReferenceRow[]): Answers {
  const assignees: Record<string, string | undefined> = {};
  for (const row of rows) {
    if (row.kind !== 'assignee') continue;
    // Today's outcome, already chosen, so it stays one press away.
    for (const stepId of row.stepIds) assignees[stepId] = LEAVE_UNASSIGNED;
  }
  // Model rows are deliberately absent: nothing in the file says which
  // settings it meant, so nothing here may guess (spec 102 FR-012).
  return { profiles: {}, assignees };
}

/**
 * The blocking rows nobody has answered.
 *
 * Derived on demand rather than tracked. A stored count is a second source of
 * truth about what is on screen, and it is the one that goes stale.
 */
export function outstanding(
  rows: readonly ReferenceRow[],
  answers: Answers,
): ReferenceRow[] {
  return rows.filter((row) => row.blocking && !answered(row, answers));
}

function answered(row: ReferenceRow, answers: Answers): boolean {
  if (row.kind === 'llm_profile') return Boolean(answers.profiles[row.value]);
  return row.stepIds.every((stepId) => Boolean(answers.assignees[stepId]));
}

/** What travels with the file, or nothing at all. */
export interface AnswersRequest {
  llm_profiles?: Record<string, string>;
  assignees?: Record<string, string>;
}

/**
 * The answers as the service wants them, or `undefined` when there are none.
 *
 * `undefined` rather than an empty object, and a gate left unassigned is
 * omitted rather than sent as an explicit nobody. Both keep one promise: an
 * upload of a file that resolves sends exactly what it sent before this
 * feature existed (contracts/what-the-dialog-sends.md).
 */
export function toRequest(answers: Answers): AnswersRequest | undefined {
  const profiles: Record<string, string> = {};
  for (const [value, chosen] of Object.entries(answers.profiles)) {
    if (chosen) profiles[value] = chosen;
  }

  const assignees: Record<string, string> = {};
  for (const [stepId, chosen] of Object.entries(answers.assignees)) {
    if (chosen && chosen !== LEAVE_UNASSIGNED) assignees[stepId] = chosen;
  }

  const request: AnswersRequest = {};
  if (Object.keys(profiles).length > 0) request.llm_profiles = profiles;
  if (Object.keys(assignees).length > 0) request.assignees = assignees;
  return Object.keys(request).length > 0 ? request : undefined;
}
